import React, { useMemo } from 'react';
import { useIntegration } from '../hooks';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { useQuery, useSubscriber } from 'services/api/hooks';
import { getIntegrationCredentialsByOrganization } from '../api';
import { organizationAtom } from 'services/organization/atom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { integrationAtom } from '../atom';
import { IntegrationCredentials } from '../types';
import { Popover } from 'components/Poppover';
import CircleIcon from '@mui/icons-material/Circle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { onCreateIntegrationCredentials } from '../subscriptions';
import { Option } from 'components/Poppover/Popover';
import { useIsOrganizationGroupAdmin } from 'hooks/useIsOrganizationGroupAdmin';
import { formatAtsProviderName } from 'core/utils/formatAtsProviderName';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

const buttonContainer: SxProps = {
  color: 'white',
  borderRadius: 0,
  fontWeight: 700,
  ':hover': {
    backgroundColor: 'transparent'
  },
  textDecoration: 'underline',
  ':disabled': {
    color: 'white',
    textDecoration: 'none'
  }
};

const connectedButtonContainer: SxProps = {
  textTransform: 'capitalize',
  color: 'white',
  fontWeight: 700,
  ':hover': {
    backgroundColor: 'transparent'
  }
};

const container: SxProps = {
  display: 'flex',
  alignItems: 'center'
};

const ConnectionState = ({
  provider,
  disconnect,
  reconnectScopes,
  reconnect,
  isActive
}: {
  provider: string;
  disconnect: () => Promise<void>;
  reconnectScopes: () => Promise<void>;
  reconnect: () => Promise<void>;
  isActive: boolean;
}) => {
  const isOrganizationGroupAdmin = useIsOrganizationGroupAdmin();
  const isAtsScopeReconnectEnabled = useFlag(Flags.ATS_SCOPE_RECONNECT);

  const onOptionClick = async (option: Option) => {
    if (option.value === 'disconnect') {
      await disconnect();
    }

    if (option.value === 'reconnect') {
      await reconnect();
    }

    if (option.value === 'reconnectScopes') {
      await reconnectScopes();
    }
  };

  const { popoverOptions, iconColor } = useMemo(() => {
    if (isActive) {
      // NOTE: In order to allow for the correct scopes with the ATS
      if (isAtsScopeReconnectEnabled) {
        return {
          popoverOptions: [
            {
              value: 'reconnectScopes',
              label: 'Reconnect',
              sx: {
                color: 'primary.main',
                textDecoration: 'underline'
              }
            },
            {
              value: 'disconnect',
              label: 'Disconnect',
              sx: {
                color: 'error.main',
                textDecoration: 'underline'
              }
            }
          ],
          iconColor: 'success.contrastText'
        };
      }

      return {
        popoverOptions: [
          {
            value: 'disconnect',
            label: 'Disconnect',
            sx: {
              color: 'error.main',
              textDecoration: 'underline'
            }
          }
        ],
        iconColor: 'success.contrastText'
      };
    }

    return {
      popoverOptions: [
        {
          value: 'reconnect',
          label: 'Reconnect',
          sx: {
            color: 'primary.main',
            textDecoration: 'underline'
          }
        }
      ],
      iconColor: 'error.main'
    };
  }, [isActive, provider]);

  if (isOrganizationGroupAdmin) {
    return (
      <Popover options={popoverOptions} onOptionClick={onOptionClick}>
        <Button
          variant="tertiary"
          sx={connectedButtonContainer}
          startIcon={
            <CircleIcon
              fontSize="medium"
              sx={{
                color: iconColor
              }}
            />
          }
          endIcon={<KeyboardArrowDownIcon sx={{ color: 'white' }} />}
          disabled={!isOrganizationGroupAdmin}
        >
          {formatAtsProviderName(provider)}
        </Button>
      </Popover>
    );
  }

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <CircleIcon
        fontSize="small"
        sx={{
          color: iconColor
        }}
      />
      <Typography sx={{ fontWeight: 700, color: 'white' }}>
        {formatAtsProviderName(provider)}
      </Typography>
    </Stack>
  );
};

export const AtsConnector = () => {
  const {
    startConnectionFlow,
    isLoading: isConnecting,
    disconnect,
    reconnectScopes,
    reconnect
  } = useIntegration();
  const organization = useRecoilValue(organizationAtom);
  const [integration, setIntegration] = useRecoilState(integrationAtom);
  const isOrganizationGroupAdmin = useIsOrganizationGroupAdmin();

  const { isLoading } = useQuery<IntegrationCredentials>(
    getIntegrationCredentialsByOrganization,
    {
      organizationId: organization?.id
    },
    {
      onFetchSuccess: (data) => {
        setIntegration(data);
      }
    }
  );

  useSubscriber({
    subQuery: onCreateIntegrationCredentials,
    subParams: {},
    onFetchSuccess: async (data) => {
      const integration: IntegrationCredentials =
        data?.onCreateIntegrationCredentials;
      if (integration && organization?.id) {
        const integrationFetched =
          await getIntegrationCredentialsByOrganization({
            organizationId: organization.id
          });
        setIntegration(integrationFetched);
      }
    }
  });

  const connectionState = useMemo(() => {
    if (integration?.provider) {
      return (
        <ConnectionState
          provider={integration.provider}
          disconnect={disconnect}
          reconnectScopes={reconnectScopes}
          reconnect={reconnect}
          isActive={integration.isActive}
        />
      );
    }

    if (!isOrganizationGroupAdmin) {
      return null;
    }

    const buttonText = () => {
      if (isConnecting) {
        return 'Connecting...';
      }

      if (isLoading) {
        return 'Loading...';
      }

      return 'Connect ATS';
    };

    return (
      <Button
        variant="tertiary"
        onClick={startConnectionFlow}
        sx={buttonContainer}
        disabled={isLoading || isConnecting}
      >
        {buttonText()}
      </Button>
    );
  }, [
    disconnect,
    integration?.provider,
    isConnecting,
    isLoading,
    startConnectionFlow,
    isOrganizationGroupAdmin
  ]);

  return <Box sx={container}>{connectionState}</Box>;
};
